import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { supabase, getAuthenticatedSupabase, withAuth } from '@/lib/supabase'
import { authService, type AuthResponse, type LoginRequest, type RegisterRequest } from '@/services/auth'
import type { 
  AuthUser, 
  EnhancedUserProfile, 
  PermissionName, 
  RoleName,
  OrganizerProfile
} from '@/lib/supabase'
import type { User, CoupleProfile, PartnerInvitation, CoupleRegistrationData, CoupleInvitationResponse } from '@/types'
import cache from '@/utils/cache'

// Constants for retry logic
const MAX_RETRY_ATTEMPTS = 3
const RETRY_DELAY_BASE = 1000 // 1 second
const RETRY_BACKOFF_MULTIPLIER = 2

// Cache constants
const PROFILE_CACHE_TTL = 10 * 60 * 1000 // 10 minutes
const getCacheKey = (userId: string) => `profile:${userId}`

// Helper function for exponential backoff
const getRetryDelay = (attempt: number): number => {
  return RETRY_DELAY_BASE * Math.pow(RETRY_BACKOFF_MULTIPLIER, attempt)
}

// Helper function to determine if error is retryable
const isRetryableError = (error: unknown): boolean => {
  if (error instanceof Error) {
    const message = error.message.toLowerCase()
    return (
      message.includes('network') ||
      message.includes('timeout') ||
      message.includes('connection') ||
      message.includes('fetch')
    )
  }
  return false
}

interface AuthState {
  // Core authentication state
  isAuthenticated: boolean
  user: AuthUser | null
  profile: EnhancedUserProfile | null
  session: any | null
  
  // Loading and error states
  isLoading: boolean
  error: string | null
  retryCount: number
  lastAttempt: number | null
  
  // Legacy couple support (for backward compatibility)
  coupleProfile: CoupleProfile | null
  activeInvitations: PartnerInvitation[]
  
  // Core authentication actions
  login: (email: string, password: string) => Promise<void>
  register: (userData: RegisterRequest) => Promise<void>
  signOut: () => Promise<void>
  
  // Session management
  checkSession: () => Promise<void>
  refreshSession: () => Promise<void>
  
  // Profile management
  updateProfile: (updates: Partial<EnhancedUserProfile>) => Promise<void>
  refreshUserData: () => Promise<void>
  
  // Role and permission management
  hasPermission: (permission: PermissionName) => boolean
  hasRole: (role: RoleName) => boolean
  getRoleLevel: () => number
  getEffectiveRole: () => string
  
  // Organizer functionality
  applyForOrganizer: (applicationData: any) => Promise<void>
  
  // Admin functionality
  assignUserRole: (userId: string, role: RoleName) => Promise<void>
  removeUserRole: (userId: string, role: RoleName) => Promise<void>
  banUser: (userId: string, reason: string, expiresAt?: string) => Promise<void>
  unbanUser: (userId: string) => Promise<void>
  
  // Legacy couple functionality (for backward compatibility)
  registerCouple: (coupleData: CoupleRegistrationData) => Promise<void>
  respondToInvitation: (response: CoupleInvitationResponse) => Promise<void>
  sendPartnerInvitation: (invitation: Omit<PartnerInvitation, 'id' | 'token' | 'sentAt' | 'status'>) => Promise<void>
  loadCoupleProfile: () => Promise<void>
  updateCoupleProfile: (updates: Partial<CoupleProfile>) => Promise<void>
  dissolveCouple: () => Promise<void>
  
  // Utility actions
  clearError: () => void
  setLoading: (loading: boolean) => void
  retryLastAction: () => Promise<void>
  resetRetryCount: () => void
  
  // Legacy compatibility - deprecated
  updateUser: (updates: Partial<User>) => void
  logout: () => void
}

// Helper function to convert EnhancedUserProfile to legacy User type
const profileToLegacyUser = (profile: EnhancedUserProfile, authUser: AuthUser): User => {
  const effectiveRole = profile.effectiveRole?.role_name || 'user'
  
  // Ensure we always have a valid ID - use authUser.id as primary source
  const userId = authUser.id || profile.id || profile.user_id
  
  // Ensure we have a valid username - create one if needed
  let username = profile.username || authUser.user_metadata?.username
  if (!username && authUser.email) {
    username = authUser.email.split('@')[0]
  }
  if (!username && userId) {
    username = `user_${userId.substring(0, 8)}`
  }
  
  console.log('🔄 ProfileToLegacyUser conversion:', {
    'authUser.id': authUser.id,
    'profile.id': profile.id,
    'profile.user_id': profile.user_id,
    'finalUserId': userId,
    'profile.username': profile.username,
    'authUser.username': authUser.user_metadata?.username,
    'finalUsername': username
  })

  return {
    id: userId as any,
    email: authUser.email as any,
    username: username || '',
    displayName: profile.display_name || authUser.user_metadata?.display_name || username || 'User',
    avatar: profile.avatar_url || authUser.user_metadata?.avatar_url,
    bio: profile.bio,
    age: profile.date_of_birth ? new Date().getFullYear() - new Date(profile.date_of_birth).getFullYear() : 18,
    location: {
      city: profile.city || '',
      state: profile.state_province || '',
      country: profile.country || '',
      coordinates: profile.location_coordinates
    },
    accountType: 'individual' as const,
    role: effectiveRole,
    preferences: {
      dating: {
        ageRange: { min: profile.min_age || 18, max: profile.max_age || 99 },
        maxDistance: profile.search_distance_km || 50,
        genderPreferences: [],
        relationshipTypes: [],
        interests: profile.interests || [],
        dealBreakers: []
      },
      social: {
        contentVisibility: 'public',
        allowMessagesFrom: 'everyone',
        showOnlineStatus: true
      },
      events: {
        interestedCategories: [],
        maxTravelDistance: 25,
        notificationPreferences: []
      },
      notifications: {
        pushEnabled: true,
        emailEnabled: true,
        types: {
          matches: true,
          messages: true,
          events: true,
          social: true
        }
      }
    },
    privacy: {
      profileVisibility: profile.profile_privacy || 'members',
      showAge: profile.age_visible ?? true,
      showLocation: profile.location_privacy || 'approximate',
      showLastActive: profile.last_active_visible ?? true,
      allowLocationSharing: profile.location_visible ?? false,
      dataRetention: {
        autoDeleteMessages: false,
        autoDeleteDays: 30
      },
      blockList: [],
      restrictedList: [],
      dataSharing: {
        analytics: false,
        marketing: false,
        research: false
      },
      communications: {
        email: true,
        sms: false,
        push: true
      },
      locationServices: {
        enabled: false,
        shareWithMatches: false,
        shareInEvents: false
      },
      searchability: {
        byUsername: true,
        byEmail: false,
        byPhone: false
      },
      contentVisibility: {
        photos: profile.photo_privacy || 'members',
        posts: 'members'
      },
      interactionLimits: {
        messagesFromStrangers: true,
        eventInvitations: true
      }
    },
    verification: {
      identity: { 
        verified: profile.is_verified,
        method: profile.verification_level,
        verifiedAt: profile.is_verified ? profile.updated_at : undefined
      },
      age: { 
        verified: profile.is_verified,
        method: profile.verification_level,
        verifiedAt: profile.is_verified ? profile.updated_at : undefined
      },
      photos: { 
        verified: profile.is_verified, 
        verifiedCount: profile.is_verified ? 1 : 0,
        lastVerified: profile.is_verified ? profile.updated_at : undefined
      }
    },
    organizerStatus: profile.organizerProfile ? {
      isVerified: profile.organizerProfile.is_verified,
      verificationLevel: profile.organizerProfile.verification_status === 'approved' ? 'premium' : 'basic',
      verifiedAt: profile.organizerProfile.verified_at || '',
      verificationBadges: [],
      totalEvents: profile.organizerProfile.total_events_created,
      totalRevenue: Number(profile.organizerProfile.total_revenue),
      averageRating: Number(profile.organizerProfile.average_rating),
      totalReviews: profile.organizerProfile.total_ratings,
      joinedAt: profile.organizerProfile.created_at,
      paymentInfo: {
        payoutMethod: 'bank',
        verificationStatus: 'verified'
      }
    } : undefined,
    adminPermissions: (effectiveRole === 'admin' || effectiveRole === 'super_admin') ? {
      level: effectiveRole as any,
      canManageUsers: profile.permissions?.includes('manage_users') || false,
      canVerifyUsers: profile.permissions?.includes('verify_users') || false,
      canManageEvents: profile.permissions?.includes('manage_events') || false,
      canManageOrganizers: profile.permissions?.includes('manage_organizers') || false,
      canModerateContent: profile.permissions?.includes('moderate_content') || false,
      canViewAnalytics: profile.permissions?.includes('view_analytics') || false,
      canManagePayments: profile.permissions?.includes('manage_payments') || false,
      canManageSystem: profile.permissions?.includes('manage_system') || false,
      canAccessReports: profile.permissions?.includes('access_reports') || false,
      canManageRoles: profile.permissions?.includes('manage_roles') || false,
      assignedBy: profile.user_id as any,
      assignedAt: profile.created_at,
      lastActiveAdmin: profile.last_active_at
    } : undefined,
    createdAt: profile.created_at,
    updatedAt: profile.updated_at,
    lastActive: profile.last_active_at
  }
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      // Initial state
      isAuthenticated: false,
      user: null,
      profile: null,
      session: null,
      coupleProfile: null,
      activeInvitations: [],
      isLoading: false,
      error: null,
      retryCount: 0,
      lastAttempt: null,

      // ========================================================================
      // CORE AUTHENTICATION METHODS
      // ========================================================================
      
      login: async (email: string, password: string) => {
        console.log('🔐 AuthStore: Starting login process for', email)
        const currentState = get()
        set({ 
          isLoading: true, 
          error: null,
          lastAttempt: Date.now()
        })
        
        const attemptLogin = async (attempt: number = 0): Promise<void> => {
          try {
            console.log('🔑 AuthStore: Calling auth service login...')
            const response = await authService.login({ email, password })
            console.log('✅ AuthStore: Login response received', response)

            // Cache the profile
            if (response.profile) {
              cache.set(getCacheKey(response.user.id), response.profile, {
                ttl: PROFILE_CACHE_TTL
              })
            }

            // Convert profile to legacy user format for compatibility
            const legacyUser = profileToLegacyUser(response.profile, response.user)
            
            // Debug the user object structure
            console.log('✅ AuthStore: User object created:', {
              id: legacyUser.id,
              username: legacyUser.username,
              displayName: legacyUser.displayName,
              profileId: response.profile?.id,
              profileUserId: response.profile?.user_id,
              profileUsername: response.profile?.username,
              authUserId: response.user?.id,
              rawProfile: response.profile,
              rawAuthUser: response.user
            })
            
            // Additional debugging for profile URL construction
            console.log('🔗 ProfileToLegacyUser Debug:', {
              'profile.id': response.profile?.id,
              'profile.user_id': response.profile?.user_id,
              'authUser.id': response.user?.id,
              'profile.username': response.profile?.username,
              'authUser.username': response.user?.user_metadata?.username,
              'finalId': legacyUser.id,
              'finalUsername': legacyUser.username,
              'profileUrlWillBe': legacyUser.username ? `/profile/@${legacyUser.username}` : legacyUser.id ? `/profile/${legacyUser.id}` : '/profile'
            })
            
            // Set the authenticated state first
            set({ 
              isAuthenticated: true, 
              user: legacyUser,
              profile: response.profile,
              session: response.session,
              isLoading: false, 
              error: null,
              retryCount: 0,
              lastAttempt: null
            })
            console.log('✅ AuthStore: State updated, user authenticated')

          } catch (error) {

            const errorMessage = error instanceof Error ? error.message : 'Login failed'
            
            // Check if we should retry
            if (attempt < MAX_RETRY_ATTEMPTS && isRetryableError(error)) {
              const delay = getRetryDelay(attempt)

              set({ 
                retryCount: attempt + 1,
                error: `${errorMessage} (Retrying in ${delay/1000}s...)`
              })
              
              await new Promise(resolve => setTimeout(resolve, delay))
              return attemptLogin(attempt + 1)
            }
            
            // Final failure

            set({ 
              isLoading: false, 
              error: errorMessage,
              retryCount: attempt + 1
            })
            throw error
          }
        }
        
        // Add timeout wrapper
        const timeoutPromise = new Promise<never>((_, reject) => {
          setTimeout(() => {

            set({ isLoading: false, error: 'Login timed out. Please try again.' })
            reject(new Error('Login timeout'))
          }, 30000) // 30 second timeout
        })
        
        try {
          await Promise.race([attemptLogin(), timeoutPromise])
        } catch (error) {
          // Ensure loading is always cleared on any error
          const currentState = get()
          if (currentState.isLoading) {

            set({ isLoading: false })
          }
          throw error
        }
      },

      register: async (userData: any) => {

        set({ 
          isLoading: true, 
          error: null,
          lastAttempt: Date.now()
        })
        
        const attemptRegister = async (attempt: number = 0): Promise<void> => {
          try {

            // Map form data to RegisterRequest format
            const registerData: any = {
              email: userData.email,
              password: userData.password,
              username: userData.username,
              displayName: userData.displayName,
              age: userData.age,
              accountType: userData.accountType,
              location: userData.location,
              agreeToTerms: true,
              agreeToPrivacy: true
            }
            
            const response = await authService.register(registerData)

            // Cache the profile
            if (response.profile) {
              cache.set(getCacheKey(response.user.id), response.profile, {
                ttl: PROFILE_CACHE_TTL
              })
            }

            // Convert profile to legacy user format for compatibility
            const legacyUser = profileToLegacyUser(response.profile, response.user)

            set({ 
              isAuthenticated: true, 
              user: legacyUser,
              profile: response.profile,
              session: response.session,
              isLoading: false, 
              error: null,
              retryCount: 0,
              lastAttempt: null
            })

          } catch (error) {

            const errorMessage = error instanceof Error ? error.message : 'Registration failed'
            
            // Check if we should retry
            if (attempt < MAX_RETRY_ATTEMPTS && isRetryableError(error)) {
              const delay = getRetryDelay(attempt)

              set({ 
                retryCount: attempt + 1,
                error: `${errorMessage} (Retrying in ${delay/1000}s...)`
              })
              
              await new Promise(resolve => setTimeout(resolve, delay))
              return attemptRegister(attempt + 1)
            }
            
            // Final failure

            set({ 
              isLoading: false, 
              error: errorMessage,
              retryCount: attempt + 1
            })
            throw error
          }
        }
        
        // Add timeout wrapper
        const timeoutPromise = new Promise<never>((_, reject) => {
          setTimeout(() => {

            set({ isLoading: false, error: 'Registration timed out. Please try again.' })
            reject(new Error('Registration timeout'))
          }, 30000) // 30 second timeout
        })
        
        try {
          await Promise.race([attemptRegister(), timeoutPromise])
        } catch (error) {
          // Ensure loading is always cleared on any error
          const currentState = get()
          if (currentState.isLoading) {

            set({ isLoading: false })
          }
          throw error
        }
      },

      signOut: async () => {
        const { user } = get()
        
        try {
          await authService.signOut()
          
          // Clear profile cache on logout
          if (user?.id) {
            cache.delete(getCacheKey(user.id))
          }
          
          set({ 
            isAuthenticated: false, 
            user: null, 
            profile: null,
            session: null,
            coupleProfile: null,
            activeInvitations: [],
            error: null 
          })
        } catch (error) {

          // Clear profile cache even if server call fails
          if (user?.id) {
            cache.delete(getCacheKey(user.id))
          }
          
          // Force local sign out even if server call fails
          set({ 
            isAuthenticated: false, 
            user: null, 
            profile: null,
            session: null,
            coupleProfile: null,
            activeInvitations: [],
            error: null 
          })
        }
      },

      // ========================================================================
      // SESSION MANAGEMENT
      // ========================================================================
      
      checkSession: async () => {
        const currentState = get()
        
        // Prevent multiple concurrent session checks
        if (currentState.isLoading) {
          console.log('🔒 AuthStore: Session check already in progress, skipping')
          return
        }

        // Don't set loading state for background session checks
        // Only set loading when explicitly requested (e.g., from login/register)
        set({ error: null })
        
        // Add timeout protection
        const timeoutPromise = new Promise<never>((_, reject) => {
          setTimeout(() => {

            reject(new Error('Session check timeout'))
          }, 15000)
        })
        
        try {
          const sessionCheckPromise = async () => {
            const { data: { session }, error } = await supabase.auth.getSession()
            
            if (error) {

              set({ 
                isAuthenticated: false, 
                user: null, 
                profile: null,
                session: null,
                error: null  // Don't show error for session check failures
              })
              return
            }

            if (session?.user) {

              try {
                // Try to get profile from cache first
                let profile = cache.get<EnhancedUserProfile>(getCacheKey(session.user.id))
                
                if (!profile) {
                  // Load enhanced profile with timeout if not cached
                  profile = await authService.getEnhancedProfile(session.user.id)
                  
                  // Cache the fetched profile
                  cache.set(getCacheKey(session.user.id), profile, {
                    ttl: PROFILE_CACHE_TTL
                  })
                }
                
                // Convert profile to legacy user format
                const legacyUser = profileToLegacyUser(profile, session.user as AuthUser)

                set({ 
                  isAuthenticated: true, 
                  user: legacyUser,
                  profile,
                  session,
                  error: null
                })
              } catch (profileError) {
                console.log('⚠️ AuthStore: Profile loading failed, creating minimal user from session', profileError)
                // Create minimal user data without profile
                const minimalUser = {
                  id: session.user.id,
                  email: session.user.email,
                  username: session.user.user_metadata?.username || session.user.email?.split('@')[0] || `user_${session.user.id.substring(0, 8)}`,
                  displayName: session.user.user_metadata?.display_name || session.user.user_metadata?.username || session.user.email?.split('@')[0] || 'User',
                  role: session.user.user_metadata?.role || 'user',
                  avatar: session.user.user_metadata?.avatar_url
                } as any
                
                console.log('🔧 AuthStore: Minimal user created:', {
                  id: minimalUser.id,
                  username: minimalUser.username,
                  displayName: minimalUser.displayName,
                  sessionData: {
                    userId: session.user.id,
                    email: session.user.email,
                    userMetadata: session.user.user_metadata
                  }
                })
                
                set({ 
                  isAuthenticated: true, 
                  user: minimalUser,
                  profile: null,
                  session,
                  error: null  // Don't show profile loading errors to user
                })
              }
            } else {

              set({ 
                isAuthenticated: false, 
                user: null, 
                profile: null,
                session: null,
                error: null
              })
            }
          }
          
          await Promise.race([sessionCheckPromise(), timeoutPromise])
        } catch (error) {

          console.error('❌ AuthStore: Session check error:', error)
          set({ 
            isAuthenticated: false, 
            user: null, 
            profile: null,
            session: null,
            error: null  // Don't show session check errors to user
          })
        }
      },

      refreshSession: async () => {
        try {
          const { data, error } = await supabase.auth.refreshSession()
          
          if (error) throw error
          
          if (data.session) {
            set({ 
              session: data.session,
              user: data.user as AuthUser
            })
          }
        } catch (error) {

          // Force logout on refresh failure
          get().signOut()
        }
      },

      // ========================================================================
      // PROFILE MANAGEMENT
      // ========================================================================
      
      updateProfile: async (updates: Partial<EnhancedUserProfile>) => {
        const { user, profile } = get()
        if (!user || !profile) throw new Error('Not authenticated')

        set({ isLoading: true, error: null })
        
        try {
          const updatedProfile = await authService.updateProfile(user.id, updates)
          
          // Update cache with new profile data
          cache.set(getCacheKey(user.id), updatedProfile, {
            ttl: PROFILE_CACHE_TTL
          })
          
          // Convert updated profile to legacy user format
          const updatedUser = profileToLegacyUser(updatedProfile, user)
          
          set({ 
            profile: updatedProfile,
            user: updatedUser, // Also update the legacy user state
            isLoading: false,
            error: null
          })
        } catch (error) {
          set({ 
            isLoading: false, 
            error: error instanceof Error ? error.message : 'Failed to update profile' 
          })
          throw error
        }
      },

      refreshUserData: async () => {
        const { user, session, isLoading } = get()
        if (!user && !session?.user) {

          return
        }
        
        // Prevent multiple concurrent refreshes
        if (isLoading) {

          return
        }
        
        const userId = user?.id || session?.user?.id
        if (!userId) {

          return
        }

        set({ isLoading: true, error: null })
        
        try {
          // Try cache first
          let profile = cache.get<EnhancedUserProfile>(getCacheKey(userId))
          
          if (!profile) {
            // Fetch from server if not cached
            profile = await authService.getEnhancedProfile(userId)
            
            // Cache the fetched profile
            cache.set(getCacheKey(userId), profile, {
              ttl: PROFILE_CACHE_TTL
            })
          }
          
          // Convert to legacy user format if we have session data
          if (session?.user) {
            const legacyUser = profileToLegacyUser(profile, session.user as AuthUser)
            set({ 
              profile, 
              user: legacyUser,
              isLoading: false,
              error: null
            })
          } else {
            set({ 
              profile,
              isLoading: false,
              error: null
            })
          }

        } catch (error) {

          set({ 
            isLoading: false,
            error: 'Failed to refresh user data'
          })
          throw error
        }
      },

      // ========================================================================
      // SESSION MANAGEMENT AND VALIDATION
      // ========================================================================
      
      validateSession: async () => {
        try {

          const { supabase: client, session } = await getAuthenticatedSupabase()
          
          if (session.user.id !== get().user?.id) {

            await get().refreshUserData()
          }
          
          // Update session in store if it's different
          const currentSession = get().session
          if (!currentSession || session.access_token !== currentSession.access_token) {
            set({ session })
          }

          return true
        } catch (error) {

          get().signOut()
          return false
        }
      },

      ensureAuthenticated: async () => {
        const isAuth = get().isAuthenticated
        if (!isAuth) {
          throw new Error('User not authenticated')
        }
        
        // Validate session is still valid
        const isValid = await get().validateSession()
        if (!isValid) {
          throw new Error('Session validation failed')
        }
        
        return true
      },

      // ========================================================================
      // ROLE AND PERMISSION METHODS
      // ========================================================================
      
      hasPermission: (permission: PermissionName): boolean => {
        const { profile } = get()
        return profile?.permissions.includes(permission) || false
      },

      hasRole: (role: RoleName): boolean => {
        const { profile } = get()
        return profile?.roles.some(r => r.role_name === role) || false
      },

      getRoleLevel: (): number => {
        const { profile } = get()
        return profile?.effectiveRole?.level || 1
      },

      getEffectiveRole: (): string => {
        const { profile } = get()
        return profile?.effectiveRole?.role_name || 'user'
      },

      // ========================================================================
      // ORGANIZER FUNCTIONALITY
      // ========================================================================
      
      applyForOrganizer: async (applicationData: any) => {
        const { user } = get()
        if (!user) throw new Error('Not authenticated')

        set({ isLoading: true, error: null })
        
        try {
          await authService.applyForOrganizer(user.id, applicationData)
          
          // Refresh user data to show pending status
          await get().refreshUserData()
          
          set({ isLoading: false })
        } catch (error) {
          set({ 
            isLoading: false, 
            error: error instanceof Error ? error.message : 'Failed to apply for organizer status' 
          })
          throw error
        }
      },

      // ========================================================================
      // ADMIN FUNCTIONALITY
      // ========================================================================
      
      assignUserRole: async (userId: string, role: RoleName) => {
        const { user } = get()
        if (!user) throw new Error('Not authenticated')
        
        set({ isLoading: true, error: null })
        
        try {
          await authService.assignRole(userId, role, user.id)
          set({ isLoading: false })
        } catch (error) {
          set({ 
            isLoading: false, 
            error: error instanceof Error ? error.message : 'Failed to assign role' 
          })
          throw error
        }
      },

      removeUserRole: async (userId: string, role: RoleName) => {
        const { user } = get()
        if (!user) throw new Error('Not authenticated')
        
        set({ isLoading: true, error: null })
        
        try {
          await authService.removeRole(userId, role, user.id)
          set({ isLoading: false })
        } catch (error) {
          set({ 
            isLoading: false, 
            error: error instanceof Error ? error.message : 'Failed to remove role' 
          })
          throw error
        }
      },

      banUser: async (userId: string, reason: string, expiresAt?: string) => {
        const { user } = get()
        if (!user) throw new Error('Not authenticated')
        
        set({ isLoading: true, error: null })
        
        try {
          await authService.banUser(userId, reason, user.id, expiresAt)
          set({ isLoading: false })
        } catch (error) {
          set({ 
            isLoading: false, 
            error: error instanceof Error ? error.message : 'Failed to ban user' 
          })
          throw error
        }
      },

      unbanUser: async (userId: string) => {
        const { user } = get()
        if (!user) throw new Error('Not authenticated')
        
        set({ isLoading: true, error: null })
        
        try {
          await authService.unbanUser(userId, user.id)
          set({ isLoading: false })
        } catch (error) {
          set({ 
            isLoading: false, 
            error: error instanceof Error ? error.message : 'Failed to unban user' 
          })
          throw error
        }
      },

      // ========================================================================
      // LEGACY COUPLE FUNCTIONALITY (for backward compatibility)
      // ========================================================================
      
      registerCouple: async (coupleData: CoupleRegistrationData) => {
        // This is a placeholder for legacy compatibility

        throw new Error('Couple registration not implemented in new auth system')
      },

      respondToInvitation: async (response: CoupleInvitationResponse) => {

        throw new Error('Invitation system not implemented in new auth system')
      },

      sendPartnerInvitation: async (invitation: any) => {

        throw new Error('Invitation system not implemented in new auth system')
      },

      loadCoupleProfile: async () => {

      },

      updateCoupleProfile: async (updates: Partial<CoupleProfile>) => {

        throw new Error('Couple profiles not implemented in new auth system')
      },

      dissolveCouple: async () => {

        throw new Error('Couple profiles not implemented in new auth system')
      },

      // ========================================================================
      // LEGACY COMPATIBILITY METHODS
      // ========================================================================
      
      updateUser: (updates: Partial<User>) => {

        // For backward compatibility, try to convert and call updateProfile
        const { profile } = get()
        if (profile) {
          const profileUpdates: Partial<EnhancedUserProfile> = {
            display_name: updates.displayName,
            bio: updates.bio,
            city: updates.location?.city,
            state_province: updates.location?.state,
            country: updates.location?.country,
            interests: updates.preferences?.dating?.interests,
            min_age: updates.preferences?.dating?.ageRange?.min,
            max_age: updates.preferences?.dating?.ageRange?.max,
            search_distance_km: updates.preferences?.dating?.maxDistance,
            profile_privacy: updates.privacy?.profileVisibility as any,
            age_visible: updates.privacy?.showAge,
            location_visible: updates.privacy?.allowLocationSharing,
            last_active_visible: updates.privacy?.showLastActive
          }
          
          get().updateProfile(profileUpdates).catch(console.error)
        }
      },

      logout: () => {

        get().signOut()
      },

      // ========================================================================
      // UTILITY METHODS
      // ========================================================================
      
      clearError: () => {
        set({ error: null })
      },

      setLoading: (loading: boolean) => {
        set({ isLoading: loading })
      },

      retryLastAction: async () => {
        const state = get()
        if (state.lastAttempt && state.error && state.retryCount < MAX_RETRY_ATTEMPTS) {
          set({ 
            error: 'Please try your last action again',
            retryCount: 0
          })
        }
      },

      resetRetryCount: () => {
        set({ retryCount: 0, lastAttempt: null })
      }
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        isAuthenticated: state.isAuthenticated,
        user: state.user,
        profile: state.profile,
        session: state.session
      })
    }
  )
)

// Set up auth state listener with improved race condition handling
let isAuthStateProcessing = false
supabase.auth.onAuthStateChange(async (event, session) => {

  // Prevent concurrent auth state processing
  if (isAuthStateProcessing && event !== 'SIGNED_OUT') {

    return
  }
  
  const store = useAuthStore.getState()
  
  if (event === 'SIGNED_IN' && session?.user) {
    isAuthStateProcessing = true
    
    try {

      // Check if we already have complete data for this user
      if (store.profile && store.user?.id === session.user.id && !store.isLoading) {

        useAuthStore.setState({
          session,
          error: null,
          isAuthenticated: true
        })
        return
      }

      // Set initial authenticated state
      useAuthStore.setState({
        isAuthenticated: true,
        session,
        error: null,
        isLoading: !store.profile  // Only set loading if we don't have profile
      })
      
      // Small delay to ensure session is propagated to Supabase client
      await new Promise(resolve => setTimeout(resolve, 100))
      
      // Load enhanced profile with timeout protection
      const profilePromise = authService.getEnhancedProfile(session.user.id)
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Profile loading timeout')), 10000)
      })
      
      try {
        // Try cache first
        let profile = cache.get<EnhancedUserProfile>(getCacheKey(session.user.id))
        
        if (!profile) {
          profile = await Promise.race([profilePromise, timeoutPromise])
          
          // Cache the fetched profile
          cache.set(getCacheKey(session.user.id), profile, {
            ttl: PROFILE_CACHE_TTL
          })
        }
        
        // Convert to legacy user format
        const legacyUser = profileToLegacyUser(profile as any, session.user as AuthUser)

        useAuthStore.setState({
          user: legacyUser,
          profile: profile as any,
          error: null,
          isLoading: false
        })
      } catch (profileError) {
        console.log('⚠️ AuthStore: Auth state change - Profile loading failed, creating minimal user from session', profileError)
        // Create minimal user from session data
        const minimalUser = {
          id: session.user.id,
          email: session.user.email,
          username: session.user.user_metadata?.username || session.user.email?.split('@')[0] || `user_${session.user.id.substring(0, 8)}`,
          displayName: session.user.user_metadata?.display_name || session.user.user_metadata?.username || session.user.email?.split('@')[0] || 'User',
          role: session.user.user_metadata?.role || 'user',
          avatar: session.user.user_metadata?.avatar_url
        } as any
        
        console.log('🔧 AuthStore: Auth state change - Minimal user created:', {
          id: minimalUser.id,
          username: minimalUser.username,
          displayName: minimalUser.displayName,
          sessionData: {
            userId: session.user.id,
            email: session.user.email,
            userMetadata: session.user.user_metadata
          }
        })
        
        useAuthStore.setState({
          user: minimalUser,
          profile: null,
          error: 'Profile loading failed - using session data',
          isLoading: false
        })
      }
    } finally {
      isAuthStateProcessing = false
    }
  } else if (event === 'SIGNED_OUT') {
    isAuthStateProcessing = false
    
    // Clear any cached profile data
    const currentUser = store.user
    if (currentUser?.id) {
      cache.delete(getCacheKey(currentUser.id))
    }

    useAuthStore.setState({
      isAuthenticated: false,
      user: null,
      profile: null,
      session: null,
      coupleProfile: null,
      activeInvitations: [],
      error: null,
      isLoading: false
    })
  } else if (event === 'TOKEN_REFRESHED' && session) {

    useAuthStore.setState({
      session,
      user: session.user as AuthUser
    })
  }
})

// Initialize session check on app load with improved startup sequence
if (typeof window !== 'undefined') {
  // Add a small delay to ensure Supabase client is fully initialized
  setTimeout(() => {
    const store = useAuthStore.getState()

    // Check if we have persisted session data
    const persistedState = JSON.parse(localStorage.getItem('auth-storage') || '{}')
    if (persistedState?.state?.session) {
      console.log('📦 AuthStore: Found persisted session data')
    }
    
    // Perform silent session check (doesn't set loading state)
    console.log('🔍 AuthStore: Performing initial session check')
    store.checkSession().catch((error) => {
      console.error('❌ AuthStore: Initial session check failed:', error)
      // Silently fail - user can still login manually
    })
  }, 100)
}

// Export a legacy compatibility function
export const getLegacyUser = (): User | null => {
  const { profile, user } = useAuthStore.getState()
  if (!profile || !user) return null
  
  return profileToLegacyUser(profile, user)
}